import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from, Observable, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';
import _ from 'lodash';
import { Patient } from '../models/patient';
import { Tenant } from '../models/tenant';
import { PatientInfoDrawerComponent } from '../pages/patients/patient-detail/patient-info-drawer/patient-info-drawer.component';
import { AuxiliarService } from './auxiliar.service';
import { TranslateService } from '@ngx-translate/core';
import { TenantService } from './tenant.service';
import { DocDrawerComponent } from '../components/drawers/doc-drawer/doc-drawer.component';
import { Doc } from '../models/document';
import { DocSignDrawerComponent } from '../components/drawers/doc-sign-drawer/doc-sign-drawer.component';
import { DocSignatureType } from '../models/document-signature-type';

@Injectable({
  providedIn: 'root'
})
export class PatientService {

  constructor(
    private httpClient: HttpClient,
    private tenantService: TenantService,
    private auxiliarService: AuxiliarService,
    private translate: TranslateService,
  ) { }

  // [GET] patients
  public getPatients(tenant: Tenant): Observable<{ [index: string]: any; }> {
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.get<HttpResponse<object>>
      (
        environment.api_server + `${environment.api_endpoints.patients}/recent-list`,
        { observe: 'response', headers }
      );
  }

  // [GET] patients for dashboard/schedule
  public getPatientsDashboard(tenant: Tenant, recent: boolean): Observable<{ [index: string]: any; }> {
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.get<HttpResponse<object>>
      (
        environment.api_server + `${environment.api_endpoints.patients}/${recent ? 'recent' : 'all'}`,
        { observe: 'response', headers }
      );
  }

  // [GET] patient
  public getPatient(idPatient: string): Observable<{ [index: string]: any; }> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.get<HttpResponse<object>>
      (
        environment.api_server + `${environment.api_endpoints.patients}/${idPatient}`,
        { observe: 'response', headers }
      );
  }

  // [GET] patient by taxcode
  public getPatientByTaxcode(taxcode: string): Observable<{ [index: string]: any; }> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.get<HttpResponse<object>>
      (
        environment.api_server + `${environment.api_endpoints.patients}/taxcode/${taxcode}`,
        { observe: 'response', headers }
      );
  }

  // [GET] patient by taxcode with medical-records
  public getPatientByTaxcodeWithMedicalRecords(taxcode: string): Observable<{ [index: string]: any; }> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.get<HttpResponse<object>>
      (
        environment.api_server + `${environment.api_endpoints.patients}/taxcode/${taxcode}/with-medicalRecords`,
        { observe: 'response', headers }
      );
  }

  // [POST] create patient
  public createPatient(patient: Patient): Observable<{ [index: string]: any; }> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.post<HttpResponse<object>>
      (
        environment.api_server + environment.api_endpoints.patients,
        patient, { observe: 'response', headers }
      );
  }

  // [PATCH] update patient
  public updatePatient(patient: Patient): Observable<{ [index: string]: any; }> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.patch<HttpResponse<object>>
    (
      environment.api_server + `${environment.api_endpoints.patients}/${_.get(patient, 'id')}`,
      patient, { observe: 'response', headers }
    );
  }

  // [DELETE] delete patient
  public deletePatient(patient: Patient): Observable<{ [index: string]: any; }> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.delete<HttpResponse<object>>(
      environment.api_server + `${environment.api_endpoints.patients}/${patient.id}`,
      { observe: 'response', headers }
    );
  }

  // [GET] patient quotes
  public getPatientQuotes(patientId: string): Observable<{ [index: string]: any; }> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.get<HttpResponse<object>>
      (
        environment.api_server + `${environment.api_endpoints.quotes}/patient/${patientId}`,
        { observe: 'response', headers }
      );
  }

  // [GET] patient items (vouchers)
  public getPatientItems(patientTaxcode: string): Observable<{ [index: string]: any; }> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.get<HttpResponse<object>>
      (
        environment.api_server + `${environment.api_endpoints.quotes}/sessions-available/${patientTaxcode}`,
        { observe: 'response', headers }
      );
  }

  // [GET] patient documents
  public getPatientDocs(patientId: string): Observable<{ [index: string]: any; }> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.get<HttpResponse<object>>
      (
        environment.api_server + `${environment.api_endpoints.patients}/${patientId}/documents`,
        { observe: 'response', headers }
      );
  }

  // [POST] upload patient document
  public uploadPatientDoc(patient: Patient, docForm: Doc): Observable<{ [index: string]: any; }> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });

    return from(this.auxiliarService.generateMD5(docForm.document)).pipe(
      switchMap((md5sum: string) => {
        const formData = new FormData();
        formData.append('document', docForm.document);
        formData.append('md5sum', md5sum);
        formData.append('name', docForm.name);
        formData.append('description', docForm.description);

        return this.httpClient.post<HttpResponse<object>>(
          environment.api_server + `${environment.api_endpoints.patients}/${patient.id}/document`,
          formData, { observe: 'response', headers }
        );
      })
    );
  }

  // [DELETE] delete patient document
  public deletePatientDoc(patient: Patient, document: Doc): Observable<{ [index: string]: any; }> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.delete<HttpResponse<object>>(
      environment.api_server + `${environment.api_endpoints.patients}/${patient.id}/document/${document.id}`,
      { observe: 'response', headers }
    );
  }

  // [GET] download patient doc
  public downloadPatientDoc(patient: Patient, doc: Doc): Observable<HttpResponse<Blob>> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.get(environment.api_server + `${environment.api_endpoints.patients}/${patient.id}/document/${doc.id}`,
      { observe: 'response', headers, responseType: 'blob' });
  }

  // [GET] patient avatar
  public getPatientAvatar(patientId: string): Observable<HttpResponse<Blob>> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.get(
      environment.api_server + `${environment.api_endpoints.patients}/${patientId}/avatar`, {
        observe: 'response', headers, responseType: 'blob'
      }
    );
  }

  // [GET] patients in pending billing
  public getPendingBillingPatients(): Observable<{ [index: string]: any; }> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.get<HttpResponse<object>>
      (
        environment.api_server + `${environment.api_endpoints.patients}/recent-list`,
        { observe: 'response', headers }
      );
  }

  // [POST] upload patient avatar
  public uploadPatientAvatar(patient: Patient, blob: any): Observable<{ [index: string]: any; }> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });

    return from(this.auxiliarService.generateMD5(blob)).pipe(
      switchMap((md5sum: string) => {
        const formData = new FormData();
        formData.append('document', blob);
        formData.append('md5sum', md5sum);
        formData.append('name', patient.givenname);

        return this.httpClient.post<HttpResponse<object>>(
          environment.api_server + `${environment.api_endpoints.patients}/${patient.id}/avatar`,
          formData, { observe: 'response', headers }
        );
      })
    );
  }

  // [GET] patient document to sign
  public getPatientDoc(docType: DocSignatureType): Observable<HttpResponse<Blob>> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.get(
      environment.api_server + `${environment.api_endpoints.patients}/${docType.url}`, {
        observe: 'response', headers, responseType: 'blob'
      }
    );
  }

  // [POST] sign patient document
  public signPatientDoc(formData: any): Observable<{ [index: string]: any; }> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });

    let body: any = null;
    const patientId = _.get(formData, 'patient.id');
    if(formData.docType.id === 'pdf-attendance') {
      body = formData;
      delete body.patient;
    } else {
      body = new FormData();
      _.forEach(formData.files, (signature) => {
        body.append('images', signature);
      });
      body.append('name', formData.name);
      body.append('description', formData.description);
      if(formData.patientComments) body.append('patientComments', formData.patientComments);
      if(formData.professionalName) body.append('professionalName', formData.professionalName);
      if(formData.professionalTaxcode) body.append('professionalTaxcode', formData.professionalTaxcode);
      if(formData.begin) body.append('begin', formData.begin);
      if(formData.end) body.append('end', formData.end);
      if(_.isBoolean(formData.attendance)) body.append('attendance', formData.attendance);
      if(formData.attendanceName) body.append('attendanceName', formData.attendanceName);
      if(formData.attendanceTaxcode) body.append('attendanceTaxcode', formData.attendanceTaxcode);
    }
    
    return this.httpClient.post<HttpResponse<object>>(
      environment.api_server + `${environment.api_endpoints.patients}/${_.get(formData, 'docType.id')}/${patientId}`,
      body, { observe: 'response', headers }
    );
  }

  // open patient info detail
  public openPatientInfo(patient: Patient | undefined, edit?: boolean): Promise<Patient> {
    return this.auxiliarService.openDrawer(PatientInfoDrawerComponent, { 
      edit,
      patient,
      position: 'left',
      iconLeft: patient ? undefined : 'pi pi-heart',
      titleLeft: patient ? patient?.givenname : this.translate.instant('button.new-patient'),
      width: '600px'
    });
  }

  // open patient new doc drawer
  public openDocDetail(patient: Patient): Promise<any> {
    return this.auxiliarService.openDrawer(DocDrawerComponent, { 
      data: { patient },
      position: 'right',
      iconRight: 'pi pi-file',
      titleRight: this.translate.instant('button.new-doc'),
      width: '600px'
    });
  }

  // open patient new doc to sign drawer
  public openDocSignature(patient: Patient, docType: {id: string, name: string, icon: string, consent: boolean}): Promise<any> {
    return this.auxiliarService.openDrawer(DocSignDrawerComponent, { 
      patient,
      docType,
      position: 'right',
      iconRight: docType.icon ?? 'pi pi-file',
      titleRight: `${this.translate.instant('button.generate-document')}: ${docType.name}`,
      width: docType.id === 'pdf-attendance' ? '600px' : '1200px'
    });
  }
}
